import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Contact, PaginatedResponse } from '../../types';
import { fetchContacts, deleteContact } from '../../services/api';
import { useSuperTenant } from '../../context/SuperAdminTenantContext';
import { encodeId } from '../../utils/hashids';
import ContactForm from './ContactForm';
import Pager from '../Pager';

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const ContactList: React.FC = () => {
  const history = useHistory();
  const { tenantId } = useSuperTenant();
  const [data, setData]         = useState<PaginatedResponse<Contact> | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState<Contact | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchContacts({ page, search: search || undefined, tenant_id: tenantId || undefined });
      setData(res);
    } catch { setError('Failed to fetch contacts'); }
    finally { setLoading(false); }
  }, [page, search, tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (c: Contact) => { setEditing(c); setShowForm(true); };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this contact?')) return;
    try { await deleteContact(id); load(); }
    catch { alert('Failed to delete contact'); }
  };

  return (
    <div className="list-page">
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">Contacts</h1>
          <p className="page-meta"><strong>{data?.total ?? 0}</strong> contacts</p>
        </div>
        <div className="page-hd-right">
          <div className="filter-input-wrap">
            <svg className="filter-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input className="filter-input" placeholder="Search contacts…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={openCreate}>+ New Contact</button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="data-card">
        {loading ? <div className="spinner">Loading…</div> : (
          <>
            <div className="data-card-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Organization</th>
                    <th>Primary</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data && data.data.length > 0 ? data.data.map(c => (
                    <tr key={c.id}>
                      <td>
                        <div className="avatar-chip" style={{ cursor: 'pointer' }}
                          onClick={() => history.push(`/contacts/${encodeId(c.id)}`)}>
                          <span className="avatar-sm" style={{ background: 'var(--teal)' }}>{initials(c.name)}</span>
                          <span className="avatar-sm-name" style={{ color: 'var(--primary)' }}>{c.name}</span>
                        </div>
                      </td>
                      <td className="td-muted">{c.designation || '—'}</td>
                      <td style={{ color: 'var(--primary)', fontSize: 13 }}>{c.phone || '—'}</td>
                      <td className="td-muted">{c.email || <span>—</span>}</td>
                      <td>
                        {c.organization
                          ? <span className="company-tag">{c.organization.name}</span>
                          : <span className="td-muted">—</span>}
                      </td>
                      <td>
                        {c.is_primary
                          ? <span className="badge badge-qualified" style={{ fontSize: 11 }}>Primary</span>
                          : null}
                      </td>
                      <td>
                        <div className="row-actions">
                          <button className="row-btn" onClick={() => openEdit(c)}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            Edit
                          </button>
                          <button className="row-btn row-btn-danger" onClick={() => handleDelete(c.id)}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr className="table-empty-row">
                      <td colSpan={7}>No contacts yet. Click <strong>+ New Contact</strong> to add one.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {data && <Pager currentPage={page} lastPage={data.last_page} total={data.total} label="Contacts" onChange={setPage} />}
          </>
        )}
      </div>

      {showForm && (
        <ContactForm
          contact={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
};

export default ContactList;
